/**
 * TotpKurulumu — Profil > Güvenlik: kimlik doğrulayıcı uygulamayı (TOTP) kur (backlog #640, backend #639).
 *
 *   0) Uyarı: kuruluma başlamak SUNUCUDA KAYITLI ESKİ GİZLİ ANAHTARIN YERİNİ ONAYLANMADAN ÖNCE alır. Kimlik doğrulayıcı zaten ikinci faktörse eski
 *      uygulama hemen çalışmaz olur; kurulum bitmeden bırakılırsa giriş için kod yazılamaz (kurtarma: yöneticiden sıfırlama). Bu yüzden açık kimlik
 *      doğrulayıcıda "anladım" kutusu ister; yarım kalmış bir kurulum varsa (hasPendingTotpEnrollment) onun atılacağını da söyler.
 *      İkinci faktör zaten açıksa MEVCUT ŞİFRE de ister.
 *   1) QR (provisioningUri'den, tarayıcıda çizilir: gizli anahtar hiçbir dış servise gitmez) + elle girmek için anahtar → 6 haneli kod → /mfa/totp/confirm.
 * Anahtar ve şifre yalnız bu bileşenin belleğinde durur.
 */
import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Alert, Button, Form, Input, Label, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { totpKurulumuBaslat, totpKurulumunuOnayla } from '../../../services/mfaService';
import { mfaMesaji } from './mfaMesajlari';

const dortlu = (s) => (s || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();

const TotpKurulumu = ({ durum, onBitti, onIptal }) => {
  const { t } = useTranslation('giris');
  const acik = Boolean(durum && durum.mfaEnabled);
  const uygulamaAcik = acik && durum.mfaMethod === 'TOTP';

  const [sifre, setSifre] = useState('');
  const [anladim, setAnladim] = useState(false);
  const [anahtar, setAnahtar] = useState(null);   // { secret, provisioningUri }
  const [kod, setKod] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  async function baslat(e) {
    e.preventDefault();
    setYukleniyor(true);
    setHata(null);
    try {
      const sonuc = await totpKurulumuBaslat({ password: acik ? sifre : undefined });
      if (sonuc.ok && sonuc.data.secret && sonuc.data.provisioningUri) {
        setAnahtar({ secret: sonuc.data.secret, provisioningUri: sonuc.data.provisioningUri });
        setSifre('');
        return;
      }
      setHata(mfaMesaji(t, sonuc));
    } catch {
      setHata(t('mfa.hata.baglanti'));
    } finally {
      setYukleniyor(false);
    }
  }

  async function onayla(e) {
    e.preventDefault();
    const temiz = kod.replace(/\s+/g, '');
    if (!temiz) return;
    setYukleniyor(true);
    setHata(null);
    try {
      const sonuc = await totpKurulumunuOnayla(temiz);
      if (sonuc.ok) { onBitti(); return; }
      setKod('');
      setHata(mfaMesaji(t, sonuc));
    } catch {
      setHata(t('mfa.hata.baglanti'));
    } finally {
      setYukleniyor(false);
    }
  }

  if (anahtar) {
    return (
      <Form onSubmit={onayla} data-testid="totp-kod-adimi">
        <p className="text-muted">{t('mfa.guvenlik.totpTara')}</p>
        <div className="text-center my-3" data-testid="totp-qr">
          <QRCodeSVG value={anahtar.provisioningUri} size={192} level="M" includeMargin />
        </div>
        <p className="mb-1 small text-muted">{t('mfa.guvenlik.totpElleGir')}</p>
        <p className="mb-3"><code className="fs-6 user-select-all" data-testid="totp-anahtar">{dortlu(anahtar.secret)}</code></p>
        <Alert color="warning" fade={false} className="py-2 small">{t('mfa.guvenlik.totpBitirmeden')}</Alert>
        {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
        <div className="mb-3">
          <Label htmlFor="totp-kurulum-kod" className="form-label">{t('mfa.giris.kodUygulama')}</Label>
          <Input id="totp-kurulum-kod" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={10}
            value={kod} disabled={yukleniyor} onChange={(e) => setKod(e.target.value)} />
        </div>
        <div className="d-flex gap-2">
          <Button color="primary" type="submit" disabled={yukleniyor || !kod.trim()}>{yukleniyor ? <Spinner size="sm" /> : t('mfa.guvenlik.onayla')}</Button>
          <Button color="light" type="button" onClick={onIptal}>{t('mfa.guvenlik.vazgec')}</Button>
        </div>
      </Form>
    );
  }

  return (
    <Form onSubmit={baslat} data-testid="totp-baslangic">
      <p className="text-muted">{t('mfa.guvenlik.totpKurAciklama')}</p>
      <Alert color={uygulamaAcik ? 'danger' : 'warning'} fade={false} className="small" data-testid="totp-uyari">
        <div>{t('mfa.guvenlik.totpDegisirUyari')}</div>
        {uygulamaAcik && <div className="mt-1 fw-semibold">{t('mfa.guvenlik.totpEskiCalismaz')}</div>}
        {durum && durum.hasPendingTotpEnrollment && <div className="mt-1">{t('mfa.guvenlik.totpYarimKalmis')}</div>}
      </Alert>
      {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
      {acik && (
        <div className="mb-3">
          <Label htmlFor="totp-kurulum-sifre" className="form-label">{t('mfa.guvenlik.mevcutSifre')}</Label>
          <Input id="totp-kurulum-sifre" type="password" autoComplete="current-password" value={sifre} onChange={(e) => setSifre(e.target.value)} />
          <div className="form-text">{t('mfa.guvenlik.sifreNedeni')}</div>
        </div>
      )}
      {uygulamaAcik && (
        <div className="form-check mb-3">
          <Input className="form-check-input" type="checkbox" id="totp-anladim" checked={anladim} onChange={(e) => setAnladim(e.target.checked)} />
          <Label className="form-check-label" htmlFor="totp-anladim">{t('mfa.guvenlik.totpAnladim')}</Label>
        </div>
      )}
      <div className="d-flex gap-2">
        <Button color="primary" type="submit" disabled={yukleniyor || (acik && !sifre) || (uygulamaAcik && !anladim)}>
          {yukleniyor ? <Spinner size="sm" /> : t('mfa.guvenlik.totpBaslat')}
        </Button>
        <Button color="light" type="button" onClick={onIptal}>{t('mfa.guvenlik.vazgec')}</Button>
      </div>
    </Form>
  );
};

export default TotpKurulumu;
